import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import Image from 'next/image'
import {useRouter} from 'next/router'
import {MediaCard} from '@/products/flexible-web-toolkit/components/media-card/media-card'
import {MetaBlock} from '@/products/flexible-web-toolkit/components/meta-block'
import {Button as PlayButton} from '@/products/flexible-web-toolkit/components/button/button'
import {useTrustArcCookieValue} from '@/utilities/trustarc'
import type {Block} from '@/services/kontent-ai/types'
import {Card} from '@/products/flexible-web-toolkit/components/card'
import {CardImage} from '@/products/flexible-web-toolkit/components/card/card-image'
import {CardFooter} from '@/products/flexible-web-toolkit/components/card/card-footer'
import {loadImageFromKontentAI} from '@/image-loader'
import {addLocalePrefix} from '@/utilities/add-locale-prefix'
import {RichTextRenderer} from '@/products/flexible-web-toolkit/components/rich-text-renderer'
import type {MetaBlockContentItem} from '../meta-block'
import type {WistiaContentItem} from '../videos/wistia'
import type {VideoBlockContentItem} from '../videos/video-block'
import {
	ReusableCTAButtonComponent,
	type CtaButtonContentItem,
} from '../cta-button'
import {getVideoDetails} from '../videos/video-types'

export type ResourceCardContentItem = IContentItem<{
	title: Elements.TextElement
	description: Elements.TextElement
	richDescription: Elements.RichTextElement
	// Bad MaC not being camelCase!
	buttontext: Elements.TextElement
	metaBlock: Elements.LinkedItemsElement<MetaBlockContentItem>
	image: Elements.AssetsElement
	video: Elements.LinkedItemsElement<
		WistiaContentItem | VideoBlockContentItem
	>
	ctaButton: Elements.LinkedItemsElement<CtaButtonContentItem>
	hideFromLister: Elements.MultipleChoiceElement
	listerOrderNumber: Elements.NumberElement
	cookieFallbackImage: Elements.AssetsElement
}>

export const ResourceCardBlock: Block<ResourceCardContentItem> = ({
	block,
	...context
}) => {
	// Bad MaC not being camelCase!
	const buttonText = block.elements.buttontext || 'Play Video'
	const trustArcCookie = useTrustArcCookieValue()
	const hideVideo = trustArcCookie === '0:' ? 'hide' : ''
	const video = block.elements.video[0]
		? getVideoDetails(block.elements.video[0])
		: null

	const {locale} = useRouter()
	const metaBlock = block.elements.metaBlock[0]
	const metaBlockHref = addLocalePrefix(
		metaBlock?.elements.url ?? '',
		locale ?? ''
	)

	const richDescriptionText = block.elements.richDescription.value
	const descriptionText = block.elements.description.trim()
	const isRichDescriptionEmpty =
		!richDescriptionText || richDescriptionText === '<p><br></p>'
	const shouldRenderRich = !isRichDescriptionEmpty
	const shouldRenderPlainDescription =
		isRichDescriptionEmpty && Boolean(descriptionText)
	return (
		<>
			{block.elements.ctaButton.length > 0 ? (
				<Card
					className="flex h-full flex-col"
					metaBlock={
						<MetaBlock
							blur
							isExternalLink={
								metaBlock?.elements.openInNewTab[0]
									?.codename === 'yes'
							}
							metaBlockHref={metaBlockHref}
							metaText=""
						>
							{block.elements.metaBlock[0]?.elements.title || ''}
						</MetaBlock>
					}
					title={block.elements.title}
				>
					{block.elements.image[0] ? (
						<CardImage
							alt={block.elements.image[0].description || ''}
							className="h-48 w-full object-contain"
							src={block.elements.image[0].url}
						/>
					) : null}
					{shouldRenderRich ? (
						<RichTextRenderer
							className="mb-4 flex-grow"
							data-kontent-element-codename="rich_description"
							element={block.elements.richDescription}
							{...context}
						/>
					) : null}

					{shouldRenderPlainDescription ? (
						<p className="mb-4 flex-grow">{descriptionText}</p>
					) : null}
					<CardFooter>
						<>
							{block.elements.ctaButton[0] ? (
								<ReusableCTAButtonComponent
									block={block.elements.ctaButton[0]}
									className="w-full"
									{...context}
									variant="primary"
								/>
							) : null}
						</>
					</CardFooter>
				</Card>
			) : (
				<MediaCard
					className="h-full"
					hasPlayVideoButton
					metaBlock={
						<MetaBlock
							blur
							isExternalLink={
								metaBlock?.elements.openInNewTab[0]
									?.codename === 'yes'
							}
							metaBlockHref={metaBlockHref}
							metaText=""
						>
							{block.elements.metaBlock[0]?.elements.title || ''}
						</MetaBlock>
					}
					title={block.elements.title}
					videoElement
					videoId={hideVideo === 'hide' ? '' : video?.videoId}
					videoPosterAltText={
						block.elements.image[0]?.description || ''
					}
					videoPosterFrameUrl={
						hideVideo === 'hide' ? '' : block.elements.image[0]?.url
					}
					videoType={video?.type}
				>
					{hideVideo === 'hide' &&
						(block.elements.cookieFallbackImage[0]?.url ? (
							<div className="mb-4 mt-10 h-48 w-full flex-shrink-0">
								<Image
									alt={
										block.elements.cookieFallbackImage[0]
											?.description || ''
									}
									className="object-contain"
									height={192}
									loader={loadImageFromKontentAI}
									src={
										block.elements.cookieFallbackImage[0]
											?.url
									}
									width={336}
								/>
							</div>
						) : (
							<div className="mb-4 mt-10 h-48 w-full bg-blue-900" />
						))}
					{shouldRenderRich ? (
						<RichTextRenderer
							className="mb-4 flex-grow"
							data-kontent-element-codename="rich_description"
							element={block.elements.richDescription}
							{...context}
						/>
					) : null}

					{shouldRenderPlainDescription ? (
						<p className="mb-4 flex-grow">{descriptionText}</p>
					) : null}
					{hideVideo === 'hide' ? (
						''
					) : (
						<PlayButton className="w-full">{buttonText}</PlayButton>
					)}
				</MediaCard>
			)}
		</>
	)
}
